import { useState, useEffect } from "react";
import { useAuth } from "../../lib/AuthContext";
import {
  Users, UserPlus, CurrencyGbp, MagnifyingGlass,
  TrendUp, Crown, CircleNotch,
} from "@phosphor-icons/react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from "recharts";

const tierColors = { free: "#94a3b8", pro: "#14b8a6", team: "#a78bfa", enterprise: "#f59e0b" };

export default function AdminDashboard() {
  const { authFetch } = useAuth();
  const [stats, setStats] = useState(null);
  const [signupChart, setSignupChart] = useState([]);
  const [lookupChart, setLookupChart] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const [statsRes, signupsRes, lookupsRes] = await Promise.all([
          authFetch(`${window.location.origin}/api/admin/stats`),
          authFetch(`${window.location.origin}/api/admin/charts/signups`),
          authFetch(`${window.location.origin}/api/admin/charts/lookups`),
        ]);
        if (statsRes.ok) setStats(await statsRes.json());
        if (signupsRes.ok) setSignupChart(await signupsRes.json());
        if (lookupsRes.ok) setLookupChart(await lookupsRes.json());
      } catch (e) {
        console.error("Admin stats error:", e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [authFetch]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <CircleNotch className="w-8 h-8 text-teal animate-spin" />
      </div>
    );
  }

  const u = stats?.users || {};
  const t = stats?.tiers || {};
  const l = stats?.lookups || {};

  const tierData = [
    { name: "Free", count: t.free || 0, fill: tierColors.free },
    { name: "Pro", count: t.pro || 0, fill: tierColors.pro },
    { name: "Team", count: t.team || 0, fill: tierColors.team },
    { name: "Enterprise", count: t.enterprise || 0, fill: tierColors.enterprise },
  ];

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="admin-dashboard">
      <h1 className="font-display font-bold text-2xl text-slate-900 mb-6">Platform Overview</h1>

      {/* Users Row */}
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Users</p>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard icon={Users} label="Total Users" value={u.total} color="text-slate-700" bg="bg-slate-100" />
        <MetricCard icon={UserPlus} label="New Today" value={u.new_today} color="text-teal" bg="bg-teal/10" />
        <MetricCard icon={TrendUp} label="This Week" value={u.new_week} color="text-blue-600" bg="bg-blue-50" />
        <MetricCard icon={TrendUp} label="This Month" value={u.new_month} color="text-violet-600" bg="bg-violet-50" />
      </div>

      {/* Subscription Row */}
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Subscriptions</p>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <MetricCard label="Free" value={t.free} color="text-slate-500" bg="bg-slate-50" small />
        <MetricCard label="Pro" value={t.pro} sub={`£${(t.pro || 0) * 29} MRR`} color="text-teal" bg="bg-teal/10" small />
        <MetricCard label="Team" value={t.team} sub={`£${(t.team || 0) * 79} MRR`} color="text-violet-600" bg="bg-violet-50" small />
        <MetricCard label="Enterprise" value={t.enterprise} color="text-amber-600" bg="bg-amber-50" small />
        <MetricCard icon={CurrencyGbp} label="Total MRR" value={`£${t.mrr || 0}`} color="text-emerald-600" bg="bg-emerald-50" small />
      </div>

      {/* Activity Row */}
      <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Lookup Activity</p>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        <MetricCard icon={MagnifyingGlass} label="Lookups Today" value={l.today} color="text-navy" bg="bg-navy/5" />
        <MetricCard icon={MagnifyingGlass} label="Lookups All Time" value={l.total} color="text-slate-600" bg="bg-slate-50" />
        <MetricCard label="Suspended" value={u.suspended || 0} color="text-red-600" bg="bg-red-50" />
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <ChartCard title="New Signups (30 days)">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={signupChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip labelFormatter={d => d} />
              <Line type="monotone" dataKey="count" stroke="#14b8a6" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>

        <ChartCard title="Lookups per Day (30 days)">
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={lookupChart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip labelFormatter={d => d} />
              <Line type="monotone" dataKey="count" stroke="#6366f1" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>

      <div className="max-w-md">
        <ChartCard title="Users by Tier">
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={tierData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="name" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {tierData.map((entry, i) => (
                  <rect key={i} fill={entry.fill} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
    </div>
  );
}

function MetricCard({ icon: Icon, label, value, sub, color, bg, small }) {
  return (
    <div className={`rounded-xl border border-slate-200 bg-white ${small ? "p-4" : "p-5"}`}>
      <div className="flex items-center gap-3">
        {Icon && (
          <div className={`w-9 h-9 rounded-lg ${bg} flex items-center justify-center shrink-0`}>
            <Icon className={`w-4.5 h-4.5 ${color}`} weight="fill" />
          </div>
        )}
        <div>
          <p className={`font-bold ${small ? "text-xl" : "text-2xl"} text-slate-900`}>{value ?? "—"}</p>
          <p className="text-xs text-slate-500">{label}</p>
          {sub && <p className={`text-xs font-semibold ${color} mt-0.5`}>{sub}</p>}
        </div>
      </div>
    </div>
  );
}

function ChartCard({ title, children }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5">
      <h3 className="text-sm font-semibold text-slate-700 mb-4">{title}</h3>
      {children}
    </div>
  );
}
